import { useEffect, useState } from "react";

/**
 * <TeamMemberAvatar /> — generic circular avatar wired to the site-assets API.
 *
 * Pass an `assetKey` (e.g. "founder_photo" or "team_photo_nitesh-rajput") and
 * the component fetches meta then renders the circular img. Falls back
 * gracefully to the `fallback` slot (typically a letter) when no photo
 * exists, the admin disabled it, or the network fails.
 */
export default function TeamMemberAvatar({
    assetKey,
    size = "w-32 h-32",
    alt = "Team member",
    ringClass = "ring-4 ring-white shadow-lg",
    priority = false,
    fallback = null,
    bustKey = 0,
}) {
    const [src, setSrc] = useState(null);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        if (!assetKey) return;
        let cancelled = false;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        // Negative cache: if we've already learned this asset doesn't exist
        // this session, skip the fetch entirely. Saves an N+1 fetch storm
        // when many lead-card avatars share the same missing key prefix
        // (e.g. tutor_photo_<id> when no tutor photos are uploaded yet).
        const negKey = `at_avatar_404:${assetKey}`;
        try {
            if (!bustKey && sessionStorage.getItem(negKey)) {
                setErrored(true);
                return;
            }
        } catch { /* private mode etc. */ }
        fetch(`${base}/api/site-assets/${encodeURIComponent(assetKey)}?_=${bustKey}`, { cache: "no-store" })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((meta) => {
                if (cancelled) return;
                if (meta?.exists && meta?.visible && meta?.raw_url) {
                    setErrored(false);
                    setSrc(`${base}${meta.raw_url}`);
                    try { sessionStorage.removeItem(negKey); } catch { /* ignore */ }
                } else {
                    setErrored(true);
                    try { sessionStorage.setItem(negKey, "1"); } catch { /* ignore */ }
                }
            })
            .catch(() => { if (!cancelled) setErrored(true); });
        return () => { cancelled = true; };
    }, [assetKey, bustKey]);

    // No assetKey → fallback immediately (derived from props, not state)
    if (!assetKey) {
        return (
            <div
                data-testid="avatar-fallback-none"
                className={`${size} rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-50 ${ringClass}`}
            >
                {fallback}
            </div>
        );
    }

    if (errored || (!src && fallback !== null && fallback !== undefined)) {
        return (
            <div
                data-testid={`avatar-fallback-${assetKey || "none"}`}
                className={`${size} rounded-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-50 ${ringClass}`}
            >
                {fallback}
            </div>
        );
    }

    if (!src) {
        return (
            <div
                data-testid={`avatar-loading-${assetKey || "none"}`}
                aria-busy="true"
                className={`${size} rounded-full bg-slate-100 animate-pulse ${ringClass}`}
            />
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            data-testid={`avatar-image-${assetKey || "none"}`}
            width={768}
            height={768}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={priority ? "high" : "auto"}
            onError={() => setErrored(true)}
            className={`${size} rounded-full object-cover object-center ${ringClass}`}
        />
    );
}
